import { FileText, GitBranch, Workflow } from 'lucide-react';

import {
  Button,
  cn,
  motionClasses,
  surfaceClasses,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shigo-ui/ui';

import { DashboardFlowCardStatus, type DashboardFlowCard } from './types';

function cardIconClassName(active: boolean, running: boolean): string {
  if (active) {
    return 'bg-foreground text-background border-foreground';
  }
  if (running) {
    return 'bg-warning/15 text-warning border-warning/30';
  }
  return 'bg-muted text-muted-foreground border-border';
}

function progressBarClassName(status: DashboardFlowCardStatus): string {
  if (status === DashboardFlowCardStatus.Running) {
    return 'bg-warning';
  }
  if (status === DashboardFlowCardStatus.Waiting) {
    return 'bg-muted-foreground/40';
  }
  return 'bg-success';
}

export type DashboardFlowCardProps = {
  card: DashboardFlowCard;
  onClick?: (card: DashboardFlowCard) => void;
  onBranchClick?: (card: DashboardFlowCard) => void;
  branchExpanded?: boolean;
  progressActive?: boolean;
};

export function DashboardFlowCardView({
  card,
  onClick,
  onBranchClick,
  branchExpanded = false,
  progressActive = false,
}: DashboardFlowCardProps) {
  const active = card.status === DashboardFlowCardStatus.Active;
  const running = card.status === DashboardFlowCardStatus.Running;
  const muted = card.status === DashboardFlowCardStatus.Waiting;
  const status = statusMeta[card.status];
  const progress = card.progress ?? progressForStatus(card.status);
  return (
    <div
      className={cn(
        'group bg-background focus-visible:ring-ring relative w-[218px] overflow-hidden rounded-xl border text-left focus:outline-none focus-visible:ring-2',
        motionClasses.lift,
        surfaceClasses.card,
        surfaceClasses.cardHover,
        active || running
          ? cn('border-foreground', surfaceClasses.cardSelected)
          : 'border-border hover:border-foreground/30',
        muted && 'opacity-45',
        branchExpanded && 'ring-foreground/30 ring-2',
      )}
    >
      <div className="border-border flex h-9 items-center justify-between border-b px-3">
        <div className="flex min-w-0 items-center gap-2">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  aria-label={status.label}
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    status.dotClassName,
                    status.glowClassName,
                    card.status === DashboardFlowCardStatus.Running && motionClasses.pulse,
                  )}
                />
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                className="bg-foreground text-background border-transparent px-1.5 py-0.5 text-[10px] font-medium"
              >
                {status.label}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span className="text-muted-foreground truncate text-[10px] font-bold uppercase">
            {card.category}
          </span>
        </div>
        <div className="text-muted-foreground flex items-center gap-1 text-[10px] font-semibold tabular-nums">
          <Workflow className="h-3 w-3" />
          {card.count}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-pressed={branchExpanded}
            aria-label={`Show ${card.category} graph steps`}
            title="Show graph steps"
            onClick={(event) => {
              event.stopPropagation();
              onBranchClick?.(card);
            }}
            className={cn(
              'ml-1 flex h-5 w-5 items-center justify-center rounded',
              branchExpanded
                ? 'bg-foreground text-background'
                : 'bg-secondary text-muted-foreground hover:text-foreground',
              motionClasses.subtleControl,
            )}
          >
            <GitBranch className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <Button
        type="button"
        variant="ghost"
        aria-label={`${status.label} ${card.category} ${String(card.count)} ${card.title}`}
        className="focus-visible:ring-ring flex h-[76px] w-full items-center justify-between gap-3 px-3 py-3 text-left focus:outline-none focus-visible:ring-2"
        onClick={() => {
          onClick?.(card);
        }}
      >
        <div className="min-w-0 space-y-1">
          <span className="block truncate text-sm font-semibold">{card.title}</span>
          <span className="text-muted-foreground block truncate text-[11px] font-medium">
            {status.label} - {String(progress)}%
          </span>
        </div>
        <span
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-md border',
            cardIconClassName(active, running),
          )}
        >
          <FileText className="h-4 w-4" />
        </span>
      </Button>
      <div className="bg-muted absolute right-0 bottom-0 left-0 h-1">
        <div
          className={cn(
            'relative h-full overflow-hidden transition-[width] duration-500',
            progressBarClassName(card.status),
            running && progressActive && 'dashboard-progress-running',
          )}
          style={{ width: `${String(progress)}%` }}
        >
          {running && progressActive ? (
            <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.6),transparent)]" />
          ) : null}
        </div>
      </div>
      {branchExpanded ? (
        <div className="border-foreground bg-foreground absolute top-0 right-0 h-2 w-2 rounded-bl-md" />
      ) : null}
    </div>
  );
}

function progressForStatus(status: DashboardFlowCardStatus) {
  if (status === DashboardFlowCardStatus.Done) return 100;
  if (status === DashboardFlowCardStatus.Active) return 70;
  if (status === DashboardFlowCardStatus.Running) return 48;
  return 8;
}

const statusMeta: Record<
  DashboardFlowCardStatus,
  { label: string; dotClassName: string; glowClassName?: string }
> = {
  [DashboardFlowCardStatus.Done]: {
    label: 'COMPLETED',
    dotClassName: 'bg-success',
    glowClassName: surfaceClasses.glowSuccess,
  },
  [DashboardFlowCardStatus.Active]: {
    label: 'READY',
    dotClassName: 'bg-success',
    glowClassName: surfaceClasses.glowSuccess,
  },
  [DashboardFlowCardStatus.Running]: {
    label: 'RUNNING',
    dotClassName: 'bg-warning',
    glowClassName: surfaceClasses.glowWarning,
  },
  [DashboardFlowCardStatus.Waiting]: {
    label: 'WAITING',
    dotClassName: 'bg-muted-foreground/50',
  },
};
